import { useQuery } from "@tanstack/react-query";
import { apiCall } from "../lib/apiClient";
import type {
  AvailableBudgetTargetsResponse,
  BudgetDetailResponse,
  BudgetListResponse,
  CardCandidatesResponse,
  UnassignedCardsResponse,
} from "../schemas/budget";

/**
 * UC-002 budget admin queries.
 *
 * Read queries — list, detail, neznámé karty, kandidáti karet. Jednotka
 * rozpočtu = tankovací karta (spec-proposal 2026-05-29-02). Per princip #4
 * detail response obsahuje `sync_complete` flag pro banner stavu.
 */

const KEY_LIST = ["admin", "budgets", "list"] as const;
const KEY_DETAIL = (id: string) => ["admin", "budgets", "detail", id] as const;
const KEY_UNASSIGNED = (id: string) =>
  ["admin", "budgets", "unassigned-cards", id] as const;
const KEY_CARD_CANDIDATES = (budgetId: string) =>
  ["admin", "budgets", "card-candidates", budgetId] as const;
const KEY_AVAILABLE_TARGETS = ["admin", "budgets", "available-targets"] as const;

export function useBudgetList() {
  return useQuery({
    queryKey: KEY_LIST,
    queryFn: () => apiCall<BudgetListResponse>("/api/admin/budgets"),
  });
}

export function useBudgetDetail(monthId: string | undefined) {
  return useQuery({
    queryKey: KEY_DETAIL(monthId ?? "—"),
    queryFn: () =>
      apiCall<BudgetDetailResponse>(`/api/admin/budgets/${monthId}`),
    enabled: typeof monthId === "string" && monthId.length > 0,
  });
}

export function useBudgetUnassignedCards(
  monthId: string | undefined,
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: KEY_UNASSIGNED(monthId ?? "—"),
    queryFn: () =>
      apiCall<UnassignedCardsResponse>(
        `/api/admin/budgets/${monthId}/unassigned-cards`,
      ),
    enabled:
      typeof monthId === "string" &&
      monthId.length > 0 &&
      (options?.enabled ?? true),
  });
}

export function useBudgetCardCandidates(
  budgetId: string | undefined,
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: KEY_CARD_CANDIDATES(budgetId ?? "—"),
    queryFn: () =>
      apiCall<CardCandidatesResponse>(
        `/api/admin/budgets/${budgetId}/card-candidates`,
      ),
    enabled:
      typeof budgetId === "string" &&
      budgetId.length > 0 &&
      (options?.enabled ?? true),
  });
}

export function useAvailableBudgetTargets(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: KEY_AVAILABLE_TARGETS,
    queryFn: () =>
      apiCall<AvailableBudgetTargetsResponse>(
        "/api/admin/budgets/available-targets",
      ),
    enabled: options?.enabled ?? true,
    staleTime: 30_000,
  });
}

export const BUDGET_QUERY_KEYS = {
  list: KEY_LIST,
  detail: KEY_DETAIL,
  unassigned: KEY_UNASSIGNED,
  cardCandidates: KEY_CARD_CANDIDATES,
  availableTargets: KEY_AVAILABLE_TARGETS,
};
